
import { AfterViewInit, Component, Input, OnDestroy, OnInit, TemplateRef, ViewChild } from '@angular/core';

import { Observable, of, Subscription, combineLatest } from 'rxjs';
import { filter,catchError, map } from 'rxjs/operators';
import { MenuItem, MenuItemCommandEvent } from 'primeng/api';
import moment from 'moment-timezone';

import { CoreDialogService } from '../../../../Core/Dialogs/services/dialog.service';
import { AbstractDialogComponent } from '../../../../Core/Dialogs/components/AbstractDialog/abstract-dialog.component';
import { FullProfileAnimationModel } from '../../../../Core/Dialogs/models/full-profile-animation.model';
import { Digest } from '@Monitor/Briefings/models/digest.model';
import { DigestService } from '@Monitor/Shared/services/digest.service';
import { ButtonTypesEnum } from '@Common/Forms/enums/button-types.enum';
import { TextInputDialogModel } from '@Common/Dialogs/models/text-input-dialog.model';
import { TextInputDialogComponent } from '@Common/Dialogs/components/TextInputDialog/text-input-dialog.component';
import { TableColumnModel } from '@Common/Forms/models/table-column.model';
import { TableColumnInputEnum } from '@Common/Forms/enums/table-column-input-type.enum';
import { UsersService } from '@Core/Users/services/users.service';
import { DialogIoModel } from '@Core/Dialogs/models/dialog-io.model';
import { DigestSubscriberDialogComponent } from '../DigestSubscriberDialog/digest-subscriber-dialog.component';
import { DigestShareDialogComponent } from '../DigestShareDialog/digest-share-dialog.component';
import { ConfigService } from '@Core/Config/services/config.service';
import { CurrentUserStateService } from '../../../../Core/Users/services/current-user-state.service';
import { DigestRow } from '@Common/MonitorShared/model/digest-row.model';


@Component({
    selector: 'agpr-manage-digests-dialog',
    templateUrl: './manage-digests-dialog.component.html',
    styleUrls: ['./manage-digests-dialog.component.scss'],
    standalone: false
})
export class ManageDigestsDialogComponent extends AbstractDialogComponent implements OnInit, AfterViewInit, OnDestroy {

    @Input() overlayContainer: Element;
    @Input() animationTrigger: Observable<FullProfileAnimationModel>;

    @ViewChild('customActionsCell') public customActionsCellRef: TemplateRef<any>;

    hideSlideOverlay: boolean = false;

    columnDefs: TableColumnModel[] = [];

    digestRows: DigestRow[] = [];
    actionOnRowData: DigestRow = null;

    hasSelectedDigests: boolean = false;

    readonly id = 'manageDigestsDialog';
    readonly nameColumn: TableColumnModel = {
        heading: 'Briefings.digest.table.name',
        field: 'name',
        tooltipField: 'name',
        fieldMaxLength: 64,
        sortable: true,
        sortType: 'text',
        type: TableColumnInputEnum.text,
        cellStyleClass: 'agpr-manageDigestDialog-digestName',
        selectable: true,
        selectField: 'selected',
        width: 200
    };
    readonly createdByColumn: TableColumnModel = {
        heading: 'Briefings.digest.table.createdBy',
        field: 'createdByUser',
        tooltipField: 'createdByUser',
        fieldMaxLength: 64,
        sortable: true,
        sortType: 'text',
        cellStyleClass: 'agpr-manageDigestDialog-digestCreatedBy',
        width: 170
    };
    readonly createdDateColumn: TableColumnModel = {
        heading: 'Briefings.digest.table.createdDate',
        field: 'createdDate',
        sortable: true,
        sortField: 'createdDateRaw',
        sortType: 'numeric',
        width: 170
    };
    readonly modifiedByColumn: TableColumnModel = {
        heading: 'Briefings.digest.table.lastModifiedBy',
        field: 'modifiedByUser',
        tooltipField: 'modifiedByUser',
        fieldMaxLength: 64,
        sortable: true,
        sortType: 'text',
        cellStyleClass: 'agpr-manageDigestDialog-digestModifiedBy',
        width: 170
    };
    readonly modifiedDateColumn: TableColumnModel = {
        heading: 'Briefings.digest.table.lastModifiedDate',
        field: 'modifiedDate',
        sortable: true,
        sortField: 'modifiedDateRaw',
        sortType: 'numeric',
        width: 170
    };
    readonly customActionsColumn: TableColumnModel = {
        type: TableColumnInputEnum.custom,
        heading: 'Briefings.digest.table.actions',
        cellStyleClass: 'agpr-manageDigestDialog-customActions',
        width: 120
    };
    readonly digestActions: MenuItem[] = [
        {
            label: 'Briefings.digest.table.actions.share',
            command: (event: MenuItemCommandEvent) => {
                this.shareDigest(this.actionOnRowData.digest);
                event?.originalEvent?.stopImmediatePropagation();
            }
        },
        {
            label: 'Briefings.digest.table.actions.view',
            command: (event: MenuItemCommandEvent) => {
                this.viewDigest(this.actionOnRowData.digest);
                event?.originalEvent?.stopImmediatePropagation();
            }
        },
        // @TODO add manage subscribers in future sprints
        // {
        //     label: 'Briefings.digest.table.actions.subscribers',
        //     command: (event: MenuItemCommandEvent) => {
        //         this.manageSubscribers(this.actionOnRowData.digest);
        //         event?.originalEvent?.stopImmediatePropagation();
        //     }
        // },
        {
            label: 'Briefings.digest.table.actions.delete',
            command: (event: MenuItemCommandEvent) => {
                this.digestService.openDeleteConfirmationDialog([this.actionOnRowData.digest]);
                event?.originalEvent?.stopImmediatePropagation();
            }
        },
    ];
    readonly buttonTypes = ButtonTypesEnum;

    private dateFormat: string;
    private subscriptions: Subscription[] = [];

    constructor(
        public coreDialogService: CoreDialogService,
        private digestService: DigestService,
        private usersService: UsersService,
        private configService: ConfigService,
        private userStateService: CurrentUserStateService,
    ) {
        super(coreDialogService, 'ManageDigests');
    }

    ngOnInit() {
        this.subscriptions.push(
            combineLatest([
                this.digestService.all$,
                this.usersService.accountUsers$,
                this.userStateService.getPreferences().pipe(filter(x => !!x))
            ]).subscribe(([digests, users, prefs]) => {
                this.dateFormat = prefs.getDateFormatString(true);

                this.digestRows = digests.map(digest => {
                    const findUserName = (id: number) => users.find(u => u.id === id)?.getDisplayName() ?? null;

                    const digestRow = new DigestRow({
                        digest: digest.clone(),
                        createdDate: moment(digest.createdDate).format(this.dateFormat),
                        createdByUser: findUserName(digest.createdByUserId),
                        modifiedByUser: findUserName(digest.lastModifiedByUserId ? digest.lastModifiedByUserId : digest.createdByUserId),
                        modifiedDate: moment(digest.lastModifiedDate ? digest.lastModifiedDate : digest.createdDate).format(this.dateFormat),
                        createdDateRaw: digest.createdDate.getTime(),
                        modifiedDateRaw: digest.lastModifiedDate ? digest.lastModifiedDate.getTime() : digest.createdDate?.getTime()
                    });

                    return digestRow;
                }) ?? [];
            })
        );
    }

    ngAfterViewInit() {
        requestAnimationFrame(() => {
            this.customActionsColumn.customTemplate = this.customActionsCellRef;

            this.columnDefs = [
                this.nameColumn,
                this.createdByColumn,
                this.createdDateColumn,
                this.modifiedByColumn,
                this.modifiedDateColumn,
                this.customActionsColumn
            ];
        });
    }

    actionOnRow(row: DigestRow){
        this.actionOnRowData = row;
    }

    createDigest() {
        const io: TextInputDialogModel = {
            inputs: {
                heading: 'Briefings.manageDigestDialog.create',
                description: 'Briefings.manageDigestDialog.create.label',
                placeholder: 'Briefings.manageDigestDialog.create.placeholder',
                okLabel: 'Briefings.manageDigestDialog.create',
                cancelLabel: 'cancel',
                width: 300,
                observableFormValidator: (name: string) => {
                    return this.digestService.isNameDuplicated$(name).pipe(
                        map((isDuplicate) => {
                            if (isDuplicate) {
                                return ['Briefings.digest.duplicateName.form.error'];
                            } else {
                                return [];
                            }
                        })
                    );
                },
                submit: (name: string) => {
                    if (name) {
                        const newDigest = new Digest();
                        newDigest.name = name;

                        return this.digestService.create(newDigest);
                    }
                    return of(null);
                }
            },
            outputs: {}
        };
        this.coreDialogService.open(TextInputDialogComponent, io);
    }

    updateDigest(digestRow: DigestRow) {
        const oldName = digestRow.digest.name;

        if (digestRow.name === digestRow.digest.name || !digestRow.name.trim()) {
            digestRow.name = oldName;
            return;
        }

        digestRow.digest.name = digestRow.name;

        this.digestService.update(digestRow.digest).pipe(
            catchError((error) => {
                digestRow.name = oldName;
                digestRow.digest.name = oldName;

                throw error;
            })
        ).subscribe();
    }

    manageSubscribers(digest: Digest) {
        const io: DialogIoModel = {
            inputs: {
                digest: digest
            },
            outputs: {}
        };

        this.coreDialogService.open(DigestSubscriberDialogComponent, io);
    }

    shareDigest(digest: Digest) {
        const io: DialogIoModel = {
            inputs: {
                digest: digest
            },
            outputs: {}
        };

        this.coreDialogService.open(DigestShareDialogComponent, io);
    }

    viewDigest(digest: Digest) {
        window.open(`${this.configService.getConfig().bmieUrl}briefings/digests/${digest.guid}`, '_blank');
    }


    cancel() {
        this.hideSlideOverlay = true;
        this.closeDialog();
    }

    deleteDigests() {
        const selectedRows = this.digestRows.filter(row => row.selected);
        const selectedFeeds = selectedRows.map(row => row.digest);
        this.digestService.openDeleteConfirmationDialog(selectedFeeds);
    }

    checkForSelectedFeeds() {
        this.hasSelectedDigests = !!this.digestRows.find(row => row.selected);
    }


    ngOnDestroy() {
        this.subscriptions.forEach(sub => sub.unsubscribe());
    }
}
